import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { t } from "i18next";
import { Button } from "@mui/material";

import {
  moveToTop,
  setIsOpen,
  getIsMaximized,
  getShowFeedback,
  getResetBtnColor,
  unitSliceData,
  selectIsPhotoTourScreen,
  selectIsVideoTourScreen,
  setIsMaximized,
  getIsParentView,
  setIsPhotoTourScreen,
  setIsVideoTourScreen,
  setIsUnitTourScreen,
  getIsMobileWindow,
  getMultiLanguage,
  selectPropertyId,
  resetLabelItems,
  selectMessages,
  setConversationStopped,
  setLoadedFromStorage,
  resetScheduleTourState,
} from "slices";
import { CustomTooltip, Icon } from "components";
import { restartChatHandle, reportIds } from "utils";
import { LanguageSwitcher } from "components/HeaderBar/LanguageSwitcher";

const ButtonsContainer = ({ setIsModal, isModal, handleReportTable, isPerformanceReport, isFaq }) => {
  const dispatch = useDispatch();
  const messages = useSelector(selectMessages);
  const isMaximized = useSelector(getIsMaximized);
  const isVideoTourScreen = useSelector(selectIsVideoTourScreen);
  const isPhotoTourScreen = useSelector(selectIsPhotoTourScreen);
  const { isUnitTourScreen } = useSelector(unitSliceData);
  const color = useSelector(getResetBtnColor);
  const isFeedbackShow = useSelector(getShowFeedback);
  const isParentView = useSelector(getIsParentView);
  const isMobile = useSelector(getIsMobileWindow);

  const propertyId = useSelector(selectPropertyId);
  const isMultiLanguageEnabled = useSelector(getMultiLanguage);

  const closeChatWindow = () => {
    dispatch(setIsOpen(false));

    if (messages.length > 2) {
      dispatch(setConversationStopped(true));
      dispatch(setLoadedFromStorage(true));
    } else {
      dispatch(setConversationStopped(false));
    }
  };

  const toggleWindowSize = () => {
    if (isMaximized) {
      dispatch(setIsPhotoTourScreen(false));
      dispatch(setIsVideoTourScreen(false));
      dispatch(setIsUnitTourScreen(false));
    }

    dispatch(setIsMaximized(!isMaximized));
    dispatch(moveToTop());
  };

  const isMax = isMaximized || isVideoTourScreen || isPhotoTourScreen || isUnitTourScreen;

  const hoverClass =
    isMax || isMobile
      ? "hayley-text-secondary hover:hayley-bg-custom-12"
      : `hayley-text-white ${!isMobile && "hover:hayley-bg-white hover:hayley-bg-opacity-20"}`;

  return (
    <div className="hayley-flex hayley-items-center hayley-gap-x-3 hayley-text-inherit">
      {reportIds.includes(propertyId) && isFaq && (
        <CustomTooltip title={t("performanceReport")} placement="bottom">
          <span
            className={`hayley-flex hayley-h-8 hayley-w-8 hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-rounded-full`}
            style={{
              backgroundColor: isPerformanceReport ? (isMax || isMobile ? color : "#fff") : "transparent",
              color: isPerformanceReport ? (isMax || isMobile ? "#fff" : color) : "inherit",
            }}
            onClick={handleReportTable}
          >
            <Icon iconType="BugIcon" className="hayley-h-4 hayley-w-4 hayley-text-inherit" />
          </span>
        </CustomTooltip>
      )}

      <div data-testid="restart_chat">
        <CustomTooltip title={t("restartChat")} placement="bottom">
          <span className="hayley-flex hayley-items-center">
            <Button
              className={`hayley-custom hayley-flex hayley-h-8 hayley-w-8 hayley-min-w-[32px] hayley-cursor-pointer hayley-items-center hayley-justify-center hayley-border-none hayley-bg-transparent hayley-p-0 hayley-text-inherit ${hoverClass} hayley-outline-none`}
              sx={{ "&.hayley-custom": { "&:hover": { color: `${color} !important` } } }}
              onClick={() => {
                if (isMaximized) {
                  dispatch(setIsPhotoTourScreen(false));
                  dispatch(setIsVideoTourScreen(false));
                  dispatch(setIsUnitTourScreen(false));
                }

                restartChatHandle();
                dispatch(resetLabelItems());
                dispatch(resetScheduleTourState());
              }}
              disabled={false}
            >
              <Icon iconType="Reload" className="hayley-h-[16px] hayley-w-[16px] hayley-text-inherit" />
            </Button>
          </span>
        </CustomTooltip>
      </div>

      {isFeedbackShow && (
        <div
          data-testid="give_feedback"
          className={`hayley-flex hayley-h-8 hayley-w-8 hayley-cursor-pointer hayley-items-center hayley-justify-center ${hoverClass}`}
        >
          <CustomTooltip title={t("giveFeedback")} placement="bottom">
            <button
              className={`hayley-m-0 hayley-h-7 hayley-w-7 hayley-cursor-pointer hayley-border-none hayley-p-0 hayley-px-1 hayley-pt-1  ${
                isModal ? (isMax ? "hayley-bg-custom-12" : "hayley-bg-white hayley-bg-opacity-20") : "hayley-bg-transparent"
              } hayley-rounded-sm hayley-outline-none ${isMax || isMobile ? "hayley-text-secondary" : "hayley-text-white"}`}
              onClick={() => setIsModal(!isModal)}
            >
              <Icon iconType="feedback" className="hayley-h-[18px] hayley-w-[20px] hayley-text-inherit" />
            </button>
          </CustomTooltip>
        </div>
      )}

      {isMultiLanguageEnabled ? <LanguageSwitcher /> : null}

      {isParentView ? null : (
        <>
          {!isMobile && (
            <CustomTooltip title={isMaximized ? t("exitFullScreen") : t("goFullScreen")} placement="bottom">
              <button
                className={`hayley-custom ${
                  isVideoTourScreen || isPhotoTourScreen || isUnitTourScreen ? "hayley-hidden md:hayley-flex " : "hayley-flex "
                } hayley-h-8 hayley-w-8 hayley-cursor-pointer hayley-items-center hayley-border-none hayley-text-center ${
                  isMaximized ? "hayley-bg-white hayley-bg-opacity-20" : "hayley-bg-transparent"
                } hayley-rounded-sm hayley-p-1 hayley-outline-none ${hoverClass}`}
                onClick={toggleWindowSize}
              >
                <Icon iconType={isMaximized ? "Resize2" : "Resize"} className="hayley-h-full hayley-w-full hayley-text-inherit" />
              </button>
            </CustomTooltip>
          )}

          <CustomTooltip title={t("minimise")} placement="bottom">
            <button
              className={`hayley-custom hayley-h-8 hayley-w-8 hayley-cursor-pointer hayley-border-none hayley-bg-transparent hayley-p-0 hayley-outline-none ${hoverClass}`}
              onClick={closeChatWindow}
            >
              <Icon iconType="Minimize" className="hayley-h-4 hayley-w-4 hayley-text-inherit" />
            </button>
          </CustomTooltip>
        </>
      )}
    </div>
  );
};

export default ButtonsContainer;
